import { useState, useEffect, useRef } from 'react'
import { motion } from 'framer-motion'
import { ArrowRight, ArrowLeft, Copy, Check, Code, Activity, UserPlus, Loader2, Target, MessageSquare, ShoppingBag, ExternalLink, Eye } from 'lucide-react'
import { cn } from '@/utils/cn'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'
import { useToast } from '@/hooks/use-toast'
import type { GoalMetric } from '@/types'
import type { OnboardingPath } from './OnboardingStepProject'

interface OnboardingStepSetupProps {
  projectId: string
  projectPath: OnboardingPath
  projectPlatform: string
  liveUrl: string
  onContinue: () => void
  onBack: () => void
}

// ── Goal presets ──

interface GoalPreset {
  metric: GoalMetric
  label: string
  defaultTarget: number
}

const GOAL_PRESETS_EXISTING: GoalPreset[] = [
  { metric: 'sessions_weekly', label: 'More traffic', defaultTarget: 500 },
  { metric: 'pagespeed_mobile', label: 'Better performance', defaultTarget: 90 },
  { metric: 'conversion_rate', label: 'Higher conversions', defaultTarget: 3 },
  { metric: 'seo_score', label: 'Better SEO', defaultTarget: 80 },
  { metric: 'revenue_weekly', label: 'More sales', defaultTarget: 5000 },
]

const GOAL_PRESETS_BUILD: GoalPreset[] = [
  { metric: 'sessions_weekly', label: 'More traffic', defaultTarget: 500 },
  { metric: 'conversion_rate', label: 'Higher conversions', defaultTarget: 3 },
  { metric: 'seo_score', label: 'Better SEO', defaultTarget: 80 },
]

const MAX_GOALS = 3

// ── Signal definitions ──

interface SignalDef {
  key: string
  signalType: string
  label: string
  frequency: string
  tokenCost: string
  showWhen: (platform: string) => boolean
}

const SIGNAL_DEFS: SignalDef[] = [
  { key: 'performance', signalType: 'performance', label: 'Performance', frequency: 'weekly', tokenCost: 'free', showWhen: () => true },
  { key: 'seo', signalType: 'seo', label: 'SEO', frequency: 'monthly', tokenCost: '5 tokens', showWhen: () => true },
  { key: 'uptime', signalType: 'uptime_digest', label: 'Uptime', frequency: 'daily', tokenCost: 'free', showWhen: () => true },
  { key: 'domain_ssl', signalType: 'domain_ssl', label: 'Domain & SSL', frequency: 'daily', tokenCost: 'free', showWhen: () => true },
  { key: 'traffic_anomaly', signalType: 'traffic_anomaly', label: 'Traffic Anomalies', frequency: 'daily', tokenCost: 'free', showWhen: () => true },
  { key: 'website_content', signalType: 'website_content', label: 'Website Content', frequency: 'weekly', tokenCost: '2 tokens', showWhen: () => true },
  { key: 'brand_monitor', signalType: 'brand_monitor', label: 'Brand Monitor', frequency: 'weekly', tokenCost: '5 tokens', showWhen: () => true },
  { key: 'ai_recommendations', signalType: 'ai_recommendations', label: 'AI Recommendations', frequency: 'monthly', tokenCost: '10 tokens', showWhen: () => true },
  { key: 'shopify_theme', signalType: 'shopify_theme_audit', label: 'Shopify Theme Audit', frequency: 'weekly', tokenCost: '2 tokens', showWhen: (p) => p === 'shopify' },
  { key: 'wordpress', signalType: 'wordpress_audit', label: 'WordPress Audit', frequency: 'weekly', tokenCost: '2 tokens', showWhen: (p) => p === 'wordpress' },
]

export function OnboardingStepSetup({ projectId, projectPath, projectPlatform, liveUrl, onContinue, onBack }: OnboardingStepSetupProps) {
  const { user } = useAuth()
  const { toast } = useToast()
  const isExisting = projectPath === 'existing'

  // ── Tag manager state ──
  const [tagCopied, setTagCopied] = useState(false)

  // ── Shopify install state ──
  const [shopifyInstalling, setShopifyInstalling] = useState(false)

  // ── Brand monitoring state ──
  const [brandName, setBrandName] = useState('')
  const [brandDomain, setBrandDomain] = useState('')
  const [brandMarket, setBrandMarket] = useState('global')

  // ── Monitoring state (default ON for existing projects) ──
  const [monitoringEnabled, setMonitoringEnabled] = useState<Record<string, boolean>>({})
  const monitoringInitialized = useRef(false)

  // ── Goals state ──
  const [selectedGoals, setSelectedGoals] = useState<GoalMetric[]>([])

  // ── Context/memory state ──
  const [contextNote, setContextNote] = useState('')
  const [isScanning, setIsScanning] = useState(false)
  const scanInitialized = useRef(false)

  // ── Invite state ──
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteRole, setInviteRole] = useState<'manager' | 'client'>('client')
  const [inviteSending, setInviteSending] = useState(false)
  const [inviteSent, setInviteSent] = useState(false)

  // ── Saving state ──
  const [isSaving, setIsSaving] = useState(false)

  // ── Completion tracking ──
  const anyMonitoringEnabled = Object.values(monitoringEnabled).some(Boolean)
  const hasGoals = selectedGoals.length > 0
  const hasContext = contextNote.trim().length > 0
  const hasInvite = inviteSent
  const hasBrandName = brandName.trim().length > 0

  const completedSections: boolean[] = []
  if (isExisting) completedSections.push(anyMonitoringEnabled) // monitoring
  completedSections.push(hasGoals) // goals
  completedSections.push(hasContext) // context
  if (isExisting) completedSections.push(hasBrandName) // brand monitoring
  if (isExisting) completedSections.push(tagCopied) // tag manager
  completedSections.push(hasInvite) // invite
  const completedCount = completedSections.filter(Boolean).length
  const totalSections = completedSections.length

  // ── Visible signals ──
  const visibleSignals = SIGNAL_DEFS.filter(s => s.showWhen(projectPlatform))

  // ── Goal presets by path ──
  const goalPresets = isExisting ? GOAL_PRESETS_EXISTING : GOAL_PRESETS_BUILD

  // ── Auto-enable monitoring for existing projects ──
  useEffect(() => {
    if (!isExisting || monitoringInitialized.current) return
    monitoringInitialized.current = true

    const signals = SIGNAL_DEFS.filter(s => s.showWhen(projectPlatform))
    const enabledState: Record<string, boolean> = {}
    signals.forEach(s => { enabledState[s.key] = true })
    setMonitoringEnabled(enabledState)

    // Enable all visible signals in DB
    const rows = signals.map(s => ({
      project_id: projectId,
      signal_type: s.signalType,
      frequency: s.frequency,
      enabled: true,
    }))
    if (rows.length > 0) {
      supabase.from('project_signals').upsert(rows, { onConflict: 'project_id,signal_type' })
    }
  }, [isExisting, projectPlatform, projectId])

  // ── Auto-scan site for context ──
  useEffect(() => {
    if (!isExisting || !liveUrl || scanInitialized.current) return
    scanInitialized.current = true
    setIsScanning(true)

    supabase.functions.invoke('analyze-site-context', {
      body: {
        url: liveUrl,
        projectId,
        agency_id: user?.agency_id,
        user_id: user?.id,
      },
    }).then(({ data }) => {
      if (data?.success && data.analysis) {
        const parts: string[] = []
        if (data.analysis.summary) parts.push(data.analysis.summary)
        if (data.analysis.business_type) parts.push(`Business type: ${data.analysis.business_type}`)
        if (data.analysis.target_audience) parts.push(`Target audience: ${data.analysis.target_audience}`)
        if (data.analysis.key_features?.length) parts.push(`Key features: ${data.analysis.key_features.join(', ')}`)
        if (parts.length > 0) {
          setContextNote(parts.join('\n'))
        }
      }
    }).catch((err) => {
      console.error('Error scanning site:', err)
    }).finally(() => {
      setIsScanning(false)
    })
  }, [isExisting, liveUrl, projectId, user?.agency_id, user?.id])

  const origin = typeof window !== 'undefined' ? window.location.origin.replace('http://', 'https://') : 'https://app.framed.dev'
  const tagManagerSnippet = `<script src="${origin}/tm.js" data-framed-project="${projectId}" async></script>`

  // ── Handlers ──

  const handleCopyScript = async () => {
    try {
      await navigator.clipboard.writeText(tagManagerSnippet)
    } catch {
      const textarea = document.createElement('textarea')
      textarea.value = tagManagerSnippet
      document.body.appendChild(textarea)
      textarea.select()
      document.execCommand('copy')
      document.body.removeChild(textarea)
    }
    setTagCopied(true)
    setTimeout(() => setTagCopied(false), 2000)
  }

  const handleShopifyInstall = () => {
    if (!user) return
    setShopifyInstalling(true)
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
    window.location.href = `${supabaseUrl}/functions/v1/shopify-oauth?step=install&project_id=${projectId}&user_id=${user.id}`
  }

  const handleToggleMonitoring = async (signal: SignalDef) => {
    const newValue = !monitoringEnabled[signal.key]
    setMonitoringEnabled(prev => ({ ...prev, [signal.key]: newValue }))

    if (newValue) {
      await supabase.from('project_signals').upsert(
        { project_id: projectId, signal_type: signal.signalType, frequency: signal.frequency, enabled: true },
        { onConflict: 'project_id,signal_type' }
      )
    } else {
      await supabase
        .from('project_signals')
        .update({ enabled: false })
        .eq('project_id', projectId)
        .eq('signal_type', signal.signalType)
    }
  }

  const handleToggleGoal = (metric: GoalMetric) => {
    setSelectedGoals(prev => {
      if (prev.includes(metric)) {
        return prev.filter(m => m !== metric)
      }
      if (prev.length >= MAX_GOALS) return prev
      return [...prev, metric]
    })
  }

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!inviteEmail.trim()) return
    setInviteSending(true)
    try {
      const { error } = await supabase.functions.invoke('invite-member', {
        body: { projectId, email: inviteEmail.trim(), role: inviteRole },
      })
      if (error) throw error
      setInviteSent(true)
      toast({ title: 'Invitation sent', description: `Invited ${inviteEmail} as ${inviteRole}` })
    } catch (err) {
      console.error('Error inviting member:', err)
      toast({ variant: 'destructive', title: 'Error', description: 'Failed to send invitation' })
    } finally {
      setInviteSending(false)
    }
  }

  const handleContinue = async () => {
    setIsSaving(true)
    try {
      // Insert goals
      if (selectedGoals.length > 0 && user) {
        const goalRows = selectedGoals.map(metric => {
          const preset = goalPresets.find(p => p.metric === metric)
          return {
            project_id: projectId,
            metric,
            target_value: preset?.defaultTarget ?? 0,
            status: 'active' as const,
            created_by: user.id,
          }
        })
        await supabase.from('project_goals').insert(goalRows)
      }

      // Insert context memory
      if (contextNote.trim() && user) {
        await supabase.from('project_memory').insert({
          project_id: projectId,
          category: 'context',
          content: contextNote.trim(),
          source: 'user_stated',
          confidence: 1.0,
          created_by: user.id,
        })
      }

      // Save brand monitoring config
      if (brandName.trim() && isExisting) {
        // Extract domain from liveUrl for brand monitor exclusion
        const domainForBrand = brandDomain.trim() || (() => {
          try { return new URL(liveUrl).hostname } catch { return '' }
        })()

        await supabase.from('project_signals').upsert(
          {
            project_id: projectId,
            signal_type: 'brand_monitor',
            frequency: 'weekly',
            enabled: true,
            config: {
              brandName: brandName.trim(),
              domain: domainForBrand,
              market: brandMarket,
              keywords: [],
            },
          },
          { onConflict: 'project_id,signal_type' }
        )
      }

      onContinue()
    } catch (err) {
      console.error('Error saving setup:', err)
      toast({ variant: 'destructive', title: 'Error', description: 'Failed to save settings. Please try again.' })
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <motion.div
      key="step-setup"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3 }}
      className="max-w-lg mx-auto"
    >
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-framed-neutral-800 tracking-tight font-display mb-2">
          Configure your project
        </h2>
        <p className="text-sm text-framed-neutral-500">
          Set up the features you need. All of these are optional — you can configure them later.
        </p>
      </div>

      <div className="space-y-4">
        {/* ── Card 1: Monitoring (existing path only) ── */}
        {isExisting && (
          <div className="bg-white rounded-xl border border-framed-neutral-300 p-5">
            <div className="flex items-start gap-3">
              <div className={cn(
                'w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0',
                anyMonitoringEnabled ? 'bg-emerald-100' : 'bg-framed-neutral-100'
              )}>
                {anyMonitoringEnabled ? (
                  <Check className="w-4 h-4 text-emerald-600" />
                ) : (
                  <Activity className="w-4 h-4 text-framed-neutral-500" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-semibold text-framed-neutral-800 mb-1">
                  Enable monitoring
                </h3>
                <p className="text-xs text-framed-neutral-500 mb-3">
                  We'll check your site automatically and alert you if something needs attention.
                </p>
                <div className="space-y-2">
                  {visibleSignals.map((signal) => (
                    <label key={signal.key} className="flex items-center gap-3 cursor-pointer">
                      <button
                        type="button"
                        role="switch"
                        aria-checked={!!monitoringEnabled[signal.key]}
                        onClick={() => handleToggleMonitoring(signal)}
                        className={cn(
                          'relative w-9 h-5 rounded-full transition-colors flex-shrink-0',
                          monitoringEnabled[signal.key] ? 'bg-emerald-500' : 'bg-framed-neutral-300'
                        )}
                      >
                        <span className={cn(
                          'absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform shadow-sm',
                          monitoringEnabled[signal.key] && 'translate-x-4'
                        )} />
                      </button>
                      <span className="text-sm text-framed-neutral-700">
                        {signal.label}
                        <span className="text-framed-neutral-400"> ({signal.frequency})</span>
                        {signal.tokenCost !== 'free' && (
                          <span className="text-framed-neutral-400"> · {signal.tokenCost}</span>
                        )}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── Card 2: Goals (both paths) ── */}
        <div className="bg-white rounded-xl border border-framed-neutral-300 p-5">
          <div className="flex items-start gap-3">
            <div className={cn(
              'w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0',
              hasGoals ? 'bg-emerald-100' : 'bg-framed-neutral-100'
            )}>
              {hasGoals ? (
                <Check className="w-4 h-4 text-emerald-600" />
              ) : (
                <Target className="w-4 h-4 text-framed-neutral-500" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-semibold text-framed-neutral-800 mb-1">
                Set your goals
              </h3>
              <p className="text-xs text-framed-neutral-500 mb-3">
                Pick up to {MAX_GOALS} priorities. You can set specific targets later.
              </p>
              <div className="flex flex-wrap gap-2">
                {goalPresets.map((preset) => {
                  const isSelected = selectedGoals.includes(preset.metric)
                  const isDisabled = !isSelected && selectedGoals.length >= MAX_GOALS
                  return (
                    <button
                      key={preset.metric}
                      type="button"
                      onClick={() => handleToggleGoal(preset.metric)}
                      disabled={isDisabled}
                      className={cn(
                        'px-3.5 py-1.5 rounded-lg text-sm border-2 transition-all',
                        isSelected
                          ? 'border-framed-yellow-400 bg-framed-yellow-100/50 text-framed-neutral-800 font-medium'
                          : 'border-framed-neutral-300 text-framed-neutral-500 hover:border-framed-neutral-400',
                        isDisabled && 'opacity-40 cursor-not-allowed hover:border-framed-neutral-300'
                      )}
                    >
                      {preset.label}
                    </button>
                  )
                })}
              </div>
            </div>
          </div>
        </div>

        {/* ── Card 3: Context / Memory (both paths) ── */}
        <div className="bg-white rounded-xl border border-framed-neutral-300 p-5">
          <div className="flex items-start gap-3">
            <div className={cn(
              'w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0',
              hasContext ? 'bg-emerald-100' : 'bg-framed-neutral-100'
            )}>
              {hasContext ? (
                <Check className="w-4 h-4 text-emerald-600" />
              ) : (
                <MessageSquare className="w-4 h-4 text-framed-neutral-500" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-semibold text-framed-neutral-800 mb-1">
                Anything else we should know?
              </h3>
              <p className="text-xs text-framed-neutral-500 mb-3">
                Context helps our AI give you better recommendations.
              </p>
              {isScanning && (
                <div className="flex items-center gap-2 text-xs text-framed-neutral-500 mb-2">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  Scanning your site for context...
                </div>
              )}
              <textarea
                value={contextNote}
                onChange={(e) => setContextNote(e.target.value)}
                placeholder={
                  isScanning
                    ? 'Scanning your site...'
                    : isExisting
                      ? 'e.g. We\'re a Shopify store selling B2B industrial supplies, the site was built 2 years ago, we get ~500 visitors/week...'
                      : 'e.g. We need the site ready by March, the main competitor is X, our brand is minimal and modern...'
                }
                rows={contextNote.includes('\n') ? 5 : 3}
                className="w-full px-4 py-3 text-sm border-2 border-framed-neutral-300 rounded-lg bg-white focus:border-framed-yellow focus:ring-2 focus:ring-framed-yellow-500/30 outline-none transition-all resize-none"
              />
            </div>
          </div>
        </div>

        {/* ── Card 4: Shopify App Install (shopify platform only) ── */}
        {isExisting && projectPlatform === 'shopify' && (
          <div className="bg-white rounded-xl border border-framed-neutral-300 p-5">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 bg-green-50">
                <ShoppingBag className="w-4 h-4 text-green-600" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-semibold text-framed-neutral-800 mb-1">
                  Install Shopify app
                </h3>
                <p className="text-xs text-framed-neutral-500 mb-3">
                  Connect your Shopify store for automated analytics, theme audits, and tag management.
                </p>
                <button
                  onClick={handleShopifyInstall}
                  disabled={shopifyInstalling}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-framed-yellow text-framed-neutral-800 font-medium rounded-lg hover:bg-framed-yellow-500 hover:scale-[1.02] transition-all text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {shopifyInstalling ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <ShoppingBag className="w-4 h-4" />
                      Install on Shopify
                      <ExternalLink className="w-3.5 h-3.5" />
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Card 5: Brand Monitoring (existing path only) ── */}
        {isExisting && (
          <div className="bg-white rounded-xl border border-framed-neutral-300 p-5">
            <div className="flex items-start gap-3">
              <div className={cn(
                'w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0',
                hasBrandName ? 'bg-emerald-100' : 'bg-framed-neutral-100'
              )}>
                {hasBrandName ? (
                  <Check className="w-4 h-4 text-emerald-600" />
                ) : (
                  <Eye className="w-4 h-4 text-framed-neutral-500" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-semibold text-framed-neutral-800 mb-1">
                  Brand monitoring
                </h3>
                <p className="text-xs text-framed-neutral-500 mb-3">
                  We'll scan the web for mentions of your brand and track sentiment.
                </p>
                <div className="space-y-3">
                  <div>
                    <label className="text-xs font-medium text-framed-neutral-600 block mb-1">Brand name</label>
                    <input
                      type="text"
                      value={brandName}
                      onChange={(e) => setBrandName(e.target.value)}
                      placeholder="e.g. Acme Inc."
                      className="w-full h-9 px-3 text-sm border-2 border-framed-neutral-300 rounded-lg bg-white focus:border-framed-yellow focus:ring-2 focus:ring-framed-yellow-500/30 outline-none transition-all"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-medium text-framed-neutral-600 block mb-1">Exclude domain</label>
                      <input
                        type="text"
                        value={brandDomain}
                        onChange={(e) => setBrandDomain(e.target.value)}
                        placeholder="acme.com"
                        className="w-full h-9 px-3 text-sm border-2 border-framed-neutral-300 rounded-lg bg-white focus:border-framed-yellow focus:ring-2 focus:ring-framed-yellow-500/30 outline-none transition-all"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-framed-neutral-600 block mb-1">Market</label>
                      <select
                        value={brandMarket}
                        onChange={(e) => setBrandMarket(e.target.value)}
                        className="w-full h-9 px-3 text-sm border-2 border-framed-neutral-300 rounded-lg bg-white focus:border-framed-yellow focus:ring-2 focus:ring-framed-yellow-500/30 outline-none transition-all"
                      >
                        <option value="global">Global</option>
                        <option value="nl">Netherlands</option>
                        <option value="be">Belgium</option>
                        <option value="de">Germany</option>
                        <option value="us">United States</option>
                        <option value="uk">United Kingdom</option>
                        <option value="fr">France</option>
                        <option value="es">Spain</option>
                        <option value="it">Italy</option>
                        <option value="au">Australia</option>
                        <option value="ca">Canada</option>
                        <option value="se">Sweden</option>
                        <option value="no">Norway</option>
                        <option value="dk">Denmark</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── Card 6: Tag Manager (existing path only) ── */}
        {isExisting && (
          <div className="bg-white rounded-xl border border-framed-neutral-300 p-5">
            <div className="flex items-start gap-3">
              <div className={cn(
                'w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0',
                tagCopied ? 'bg-emerald-100' : 'bg-framed-neutral-100'
              )}>
                {tagCopied ? (
                  <Check className="w-4 h-4 text-emerald-600" />
                ) : (
                  <Code className="w-4 h-4 text-framed-neutral-500" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-semibold text-framed-neutral-800 mb-1">
                  Install tag manager
                </h3>
                <p className="text-xs text-framed-neutral-500 mb-3">
                  Add this to your site's <code className="text-xs bg-framed-neutral-100 px-1 py-0.5 rounded font-mono">&lt;head&gt;</code> to enable analytics, feedback widget, and all tracking tags.
                  {projectPlatform === 'shopify' && (
                    <span className="text-framed-neutral-400"> If you installed the Shopify app, this is already done.</span>
                  )}
                </p>
                <div className="relative">
                  <pre className="bg-framed-neutral-800 text-framed-neutral-200 text-xs p-3 rounded-lg overflow-x-auto font-mono">
                    {tagManagerSnippet}
                  </pre>
                  <button
                    onClick={handleCopyScript}
                    className="absolute top-2 right-2 p-1.5 rounded-md bg-framed-neutral-700 hover:bg-framed-neutral-600 text-framed-neutral-400 transition-colors"
                    title="Copy to clipboard"
                  >
                    {tagCopied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── Card 7: Invite (both paths) ── */}
        <div className="bg-white rounded-xl border border-framed-neutral-300 p-5">
          <div className="flex items-start gap-3">
            <div className={cn(
              'w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0',
              inviteSent ? 'bg-emerald-100' : 'bg-framed-neutral-100'
            )}>
              {inviteSent ? (
                <Check className="w-4 h-4 text-emerald-600" />
              ) : (
                <UserPlus className="w-4 h-4 text-framed-neutral-500" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-semibold text-framed-neutral-800 mb-1">
                Invite a team member
              </h3>
              <p className="text-xs text-framed-neutral-500 mb-3">
                Share this project with your team.
              </p>
              {inviteSent ? (
                <div className="flex items-center gap-2 text-sm text-emerald-600">
                  <Check className="w-4 h-4" />
                  Invitation sent to {inviteEmail}
                </div>
              ) : (
                <form onSubmit={handleInvite} className="flex gap-2">
                  <input
                    type="email"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    placeholder="colleague@example.com"
                    required
                    className="flex-1 h-9 px-3 text-sm border-2 border-framed-neutral-300 rounded-lg bg-white focus:border-framed-yellow focus:ring-2 focus:ring-framed-yellow-500/30 outline-none transition-all"
                  />
                  <select
                    value={inviteRole}
                    onChange={(e) => setInviteRole(e.target.value as 'manager' | 'client')}
                    className="h-9 px-2 text-sm border-2 border-framed-neutral-300 rounded-lg bg-white focus:border-framed-yellow focus:ring-2 focus:ring-framed-yellow-500/30 outline-none transition-all"
                  >
                    <option value="client">Client</option>
                    <option value="manager">Manager</option>
                  </select>
                  <button
                    type="submit"
                    disabled={inviteSending || !inviteEmail.trim()}
                    className="h-9 px-3 bg-framed-neutral-800 text-white text-sm rounded-lg font-medium hover:bg-framed-neutral-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {inviteSending ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Invite'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between mt-6">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-1.5 text-sm text-framed-neutral-500 hover:text-framed-neutral-700 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>
        <button
          onClick={handleContinue}
          disabled={isSaving}
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-framed-neutral-800 text-white rounded-lg font-medium hover:bg-framed-neutral-900 hover:scale-[1.02] transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
        >
          {isSaving ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              Continue
              {completedCount > 0 && (
                <span className="text-xs bg-white/20 px-1.5 py-0.5 rounded-full">{completedCount}/{totalSections}</span>
              )}
              <ArrowRight className="w-4 h-4" />
            </>
          )}
        </button>
      </div>
    </motion.div>
  )
}
